import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faChevronRight, faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import React, { useState, FC, useRef, useLayoutEffect, useCallback } from 'react';
import ResizeObserverPolyfill from 'resize-observer-polyfill';

import { addVariants, useHoldPress, useRect } from '@agilemd/ui-utils';

import Button, { ButtonProps } from '../index.ts';
import styles from './styles.module.scss';

/**
 * Horizontal overflow tabs/pathway sections: the outer node uses `useRect`, whose
 * ResizeObserver triggers React updates on resize. Previously we summed every child's
 * getBoundingClientRect() on each render to get total width — with many children that
 * forced synchronous layout during those updates and could thrash the main thread /
 * surface ResizeObserver loop warnings (see PRS-7728 in web-meta pathway toolbar).
 *
 * We track content width via the inner scroller's scrollWidth (O(1)). We remeasure
 * after every layout commit (children add/remove, title changes) and also subscribe
 * ResizeObserver on the inner node: border-box growth fires RO, but overflow-only
 * growth can change scrollWidth without a border-box change, so commit-time measure
 * covers pathway sections appearing/disappearing without relying on RO alone.
 */
type ArrowProps = {
  /** Left arrow gets icon pointing to the left, similarly for right icon */
  icon: IconDefinition;
  /** Corresponds to `ButtonProps.color` */
  color: ButtonProps['color'];
  className: string;
  onMouseDown: () => void;
  onMouseUp: () => void;
  onMouseLeave: () => void;
  onTouchStart: () => void;
  onTouchEnd: () => void;
};

export default function useScrollingArrows<T extends HTMLElement>({
  renderArrow = ({ ...arrowProps }) => <Button {...arrowProps} />,
  buttonWidth = 32,
  scrollBy = 12,
  overscrollBy = 32
}: {
  renderArrow?: FC<ArrowProps>;
  buttonWidth?: number;
  scrollBy?: number;
  overscrollBy?: number;
} = {}): {
  outsideContainerRef: React.MutableRefObject<T | undefined | null>;
  insideContainerRef: React.MutableRefObject<T | undefined | null>;
  rightArrow: React.ReactNode;
  leftArrow: React.ReactNode;
  offset: number;
  setOffset: React.Dispatch<React.SetStateAction<number>>;
  canOverscroll: boolean;
} {
  const [offset, setOffset] = useState(0);
  const [outsideContainerRef, { width: outsideContainerWidth }] = useRect<T>();
  const insideContainerRef = useRef<T | null>(null);
  const [insideContainerWidth, setInsideContainerWidth] = useState(0);

  const measureInside = useCallback(() => {
    const el = insideContainerRef.current;

    if (!el) {
      setInsideContainerWidth(0);
      return;
    }

    // scrollWidth matches total overflow width for this strip; one read vs O(n) rects.
    const w = el.scrollWidth;
    setInsideContainerWidth(prev => (prev === w ? prev : w));
  }, []);

  // Remeasure after every commit: pathway sections / tabs can mount or update without
  // the inner node's border-box changing (scrollWidth grows from overflow instead).
  useLayoutEffect(() => {
    measureInside();
  });

  useLayoutEffect(() => {
    const el = insideContainerRef.current;

    if (!el) {
      return;
    }

    const ResizeObserverCtor =
      ResizeObserverPolyfill as unknown as typeof globalThis.ResizeObserver;
    const ro = new ResizeObserverCtor(() => {
      measureInside();
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [measureInside]);

  const canOverscroll = insideContainerWidth > outsideContainerWidth;
  const shouldShowLeftArrow = offset < 0;

  const { isHolding: isHoldingLeft, ...leftHandlers } = useHoldPress(() => {
    if (!shouldShowLeftArrow) {
      return;
    }

    setOffset(o => {
      const nextOffset = o + scrollBy;
      return Math.min(nextOffset, 0);
    });
  }, shouldShowLeftArrow);
  const { isHolding: isHoldingRight, ...rightHandlers } = useHoldPress(() =>
    setOffset(o => {
      const nextOffset = o - scrollBy;
      const widthToScroll =
        insideContainerWidth - outsideContainerWidth + buttonWidth + overscrollBy;
      return Math.max(nextOffset, -1 * widthToScroll);
    })
  );
  const leftArrowProps = {
    ...leftHandlers,
    icon: faChevronLeft,
    color: 'blue-hover' as const,
    className: addVariants('aglmd-scroll__button', styles, 'left', isHoldingLeft && 'holding')
  };
  const rightArrowProps = {
    ...rightHandlers,
    icon: faChevronRight,
    color: 'blue-hover' as const,
    className: addVariants('aglmd-scroll__button', styles, 'right', isHoldingRight && 'holding')
  };

  const leftArrow = (canOverscroll &&
    shouldShowLeftArrow &&
    renderArrow(leftArrowProps)) as React.ReactNode;
  const rightArrow = (canOverscroll && renderArrow(rightArrowProps)) as React.ReactNode;

  return {
    outsideContainerRef,
    insideContainerRef,
    rightArrow,
    leftArrow,

    offset,
    setOffset,
    canOverscroll
  };
}
